import { readItemField } from './item-field-reader';
import type { ItemFieldRow, ItemFieldDependencies } from './item-field-reader';
import { readAbilityEntryId } from './inventory-ability-grant-helpers';
import { readItemUuidSlot } from './inventory-raw-readers';
import { phpJsonString } from './php-json-values';

const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const own = (value: unknown, key: string): unknown => arrayLike(value) && Object.prototype.hasOwnProperty.call(value, key)
    ? (value as Readonly<Record<string, unknown>>)[key] : undefined;
const valid = (value: string): boolean => /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(value);
const trim = (value: string): string => value.replace(/^[\x00\x09\x0a\x0b\x0d\x20]+|[\x00\x09\x0a\x0b\x0d\x20]+$/g, '');
function clone<T>(value: T): T {
    if (!arrayLike(value)) return value;
    const entries = Object.entries(value);
    return (entries.every(([key], i) => key === String(i)) ? entries.map(([, child]) => clone(child))
        : Object.fromEntries(entries.map(([key, child]) => [key, clone(child)]))) as T;
}
export interface CreatureSizeSourceBundleDependencies extends ItemFieldDependencies {
    queryFullItem: (id: string) => ItemFieldRow | null;
    resolveCarrierRef: (id: string) => ItemFieldRow | null;
    resolveItemName: (id: string) => unknown;
}
export interface CreatureSizeSourceBundle {
    item_arr: ItemFieldRow;
    source_id: string;
    source_label: unknown;
    source_kind: string;
}
/** Native fate_tide_recalc_size_source_entry_bundle selection and provenance.
 * Definition/name/identity reads stay explicit. Unknown replies must throw.
 * This resolves one source entry; it does not collect buckets or score size. */
export function resolveCreatureSizeSourceBundle(entry: ItemFieldRow | string, bucketKind: string,
    deps: CreatureSizeSourceBundleDependencies): CreatureSizeSourceBundle | null {
    if (bucketKind == null || arrayLike(bucketKind)) throw new TypeError('Native bucket kind requires a string');
    bucketKind = phpJsonString(bucketKind);
    if (entry === null) throw new TypeError('Native size source requires array|string');
    if (!arrayLike(entry)) {
        const id = trim(phpJsonString(entry));
        if (id === '' || !valid(id)) return null;
        const resolved = deps.queryFullItem(id);
        if (!arrayLike(resolved)) return null;
        return clone({ item_arr: resolved, source_id: id, source_label: deps.resolveItemName(id), source_kind: bucketKind });
    }
    let ability = own(entry, 'item_arr') ?? own(entry, 'status_arr') ?? own(entry, 'status_item_id') ?? null;
    if (typeof ability === 'string' && ability !== '' && valid(ability)) ability = deps.resolveCarrierRef(ability);
    if (!arrayLike(ability)) ability = entry;
    const row = ability as ItemFieldRow;
    const field = (target: ItemFieldRow | null, key: string): unknown => readItemField(target, key, deps);
    let id = '', label = '';
    if (bucketKind === 'status_effect') {
        id = phpJsonString(field(entry, 'item_id') ?? '');
        label = phpJsonString(field(entry, 'item_name') ?? '');
        if (label === '' && field(row, 'item_name') !== null) label = phpJsonString(field(row, 'item_name'));
    } else {
        id = readAbilityEntryId(entry, { itemField: field, resolveCarrierRef: deps.resolveCarrierRef });
        label = phpJsonString(own(entry, 'item_name') ?? '');
        const nested = own(entry, 'item_arr');
        if (label === '' && arrayLike(nested) && field(nested, 'item_name') !== null) label = phpJsonString(field(nested, 'item_name'));
    }
    if (id !== '' && !valid(id)) id = '';
    if (id === '' && bucketKind !== 'status_effect') {
        const fallback = readItemUuidSlot(field(entry, 'item_id') ?? '');
        if (fallback !== '' && valid(fallback)) id = fallback;
    }
    let finalLabel: unknown = label;
    if (label === '' && id !== '' && valid(id)) finalLabel = deps.resolveItemName(id);
    return clone({ item_arr: row, source_id: id, source_label: finalLabel, source_kind: bucketKind });
}
